import { type MaterialPracticeSummary } from '@/platform/materials/learning'

import { startedAtLabels } from './existing-session'
import { JourneyFrame, Note } from './JourneyChrome'
import { FRACTION_TOPIC_LABEL } from './TopicCheck'
import { PANEL, PRIMARY, SECONDARY } from './ui'

/**
 * After the learner confirms the topic, an unfinished set of the same topic
 * can already exist (each upload makes its own set, D-17). The learner chooses
 * to continue it or to start a new run; either way the old set stays saved.
 */

export function ExistingSessionChoice({
  sets,
  busy,
  error,
  onContinue,
  onCreateNew,
  onHome,
}: {
  /** Unfinished sets of the same topic, newest first; never empty. */
  sets: readonly MaterialPracticeSummary[]
  busy: boolean
  error: string | null
  onContinue: (setId: string) => void
  onCreateNew: () => void
  onHome: () => void
}): React.JSX.Element {
  const one = sets.length === 1
  const startedAt = startedAtLabels(sets)
  return (
    <JourneyFrame
      step="Chủ đề"
      title="Con đang học dở chủ đề này"
      onBack={null}
      onHome={onHome}
    >
      <section aria-labelledby="existing-sets" className={PANEL}>
        <h2 id="existing-sets" className="text-lg font-semibold">
          {one
            ? `Con đã có một bài “${FRACTION_TOPIC_LABEL}” chưa làm xong:`
            : `Con đã có ${String(sets.length)} bài “${FRACTION_TOPIC_LABEL}” chưa làm xong:`}
        </h2>
        <ul className="flex flex-col gap-3">
          {sets.map((s) => (
            <li
              key={s.set_id}
              className="flex flex-col gap-2 rounded-2xl border-2 border-emerald-300 bg-white p-3 dark:border-emerald-800 dark:bg-slate-900"
            >
              <p className="text-lg">
                Bắt đầu lúc {startedAt(s)} · đã làm {s.answered}/{s.items} câu
              </p>
              <button
                type="button"
                className={PRIMARY}
                disabled={busy}
                aria-label={`Tiếp tục bài cũ (bắt đầu lúc ${startedAt(s)})`}
                onClick={() => {
                  onContinue(s.set_id)
                }}
              >
                Tiếp tục bài cũ
              </button>
            </li>
          ))}
        </ul>
      </section>
      <Note tone="info">
        {one ? 'Bài cũ' : 'Các bài cũ'} vẫn được lưu, không bị xóa hay ghi đè. Nếu con tạo
        lượt học mới, bài cũ vẫn ở trong “Bài đang học dở” để con quay lại sau.
      </Note>
      {error !== null && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
        >
          {error}
        </p>
      )}
      <button type="button" className={SECONDARY} disabled={busy} onClick={onCreateNew}>
        {busy ? 'Đang chuẩn bị bài học…' : 'Tạo lượt học mới'}
      </button>
    </JourneyFrame>
  )
}
